import { type CSSProperties, useCallback, useMemo, useState } from "react";
import type { DiscoverGamePatchRequest, DiscoverGameV2Response, GameStatus } from "libs/apiClient";
import client from "libs/client";
import { Link } from "react-router";
import SelectComponent, { type SelectOption } from "ui-kit/src/SelectComponent/SelectComponent";
import { ContentCell } from "../../ContentCell";
import { DiscoverGameHistoryTab } from "./gameHistory/DiscoverGameHistoryTab";
import { DiscoverGameAnalysisTab } from "./DiscoverGameAnalysisTab";
import { DiscoverGameLinksTab } from "./DiscoverGameLinksTab";
import { DiscoverGameTeamsheetTab } from "./DiscoverGameTeamsheetTab";
import { DiscoverGameVideoTab } from "./DiscoverGameVideoTab";
import { BasicGameMetadataEditors } from "./BasicGameMetadataEditors";
import { DISCOVER_GAME_V2_DETAIL_QUERY_KEY } from "./constants";
import { DISCOVER_GAME_HISTORY_NOTES_QUERY_KEY } from "./gameHistory/constants";
import { Tabs } from "../../Tabs";
import { formatDaysHoursMinutesFromTotalSeconds } from "common/src/utils/dateTimeFormatUtils";
import { isEmptyOrNull } from "common/src/utils/stringUtils";
import { isNullOrUndefined } from "common/src/utils/valueUtils";
import {
	DiscoverGameInlinePatchField,
	DiscoverGameStatus,
	DISCOVER_GAMES_PRIORITY_FILTER_OPTIONS,
	gameStatusGradeColor,
	isPriorityEditableForStatus,
} from "../../../datagrid-configs/discoverGames";
import { useToast } from "ui-kit";
import { _ } from "i18n";
import { isGranted } from "pages/productManagement/functions";
import { Permission, Resource } from "pages/productManagement/types";
import { gameTypeLabel } from "./gameMetadataFormatters";
import "./DiscoverGameDetailV2.css";

type UserOption = { value: string; label: string };

const PRIORITY_NULL_DEFAULT = 5;

/** Matches grid: unset `gameInternal.priority` is shown as 5 in the UI. */
function discoverGameDetailDisplayPriority(priority: number | null | undefined): number {
	if (isNullOrUndefined(priority)) {
		return PRIORITY_NULL_DEFAULT;
	}
	return priority;
}

type DiscoverGameDetailInlinePatchValue = {
	[DiscoverGameInlinePatchField.Priority]: number;
	[DiscoverGameInlinePatchField.GameStatus]: GameStatus | null;
	[DiscoverGameInlinePatchField.ClubManagerId]: number | null;
	[DiscoverGameInlinePatchField.ContentManagerId]: number | null;
	[DiscoverGameInlinePatchField.AssigneeId]: number | null;
	[DiscoverGameInlinePatchField.IsVip]: boolean;
};

export function DiscoverGameDetailV2({ gameData }: { gameData: DiscoverGameV2Response }) {
	const { addSuccessToast, addErrorToast } = useToast();
	const [tab, setTab] = useState("basic");
	const [isEditingBasicGameData, setIsEditingBasicGameData] = useState(false);

	const gameUploadsPermissions = useMemo(
		() => ({
			canEditGameUploads: isGranted(Resource.GAME_UPLOADS, Permission.EDIT),
			canEditVip: isGranted(Resource.GAME_UPLOAD_SINGLE, Permission.GAME_VIP),
			canEditHidden: isGranted(Resource.GAME_UPLOAD_SINGLE, Permission.GAME_HIDDEN),
			canEditScore: isGranted(Resource.GAME_UPLOAD_SINGLE, Permission.GAME_SCORE),
			canEditAssignee: isGranted(Resource.GAME_UPLOADS, Permission.GAME_ASSIGNEE_SELECTION),
			canEditGameStatus: isGranted(Resource.GAME_UPLOADS, Permission.GAME_STATUS_EDIT),
			canViewSla: isGranted(Resource.GAME_UPLOADS, Permission.GAME_VIEW_SLA),
		}),
		[isGranted],
	);
	const {
		canEditGameUploads,
		canEditVip,
		canEditHidden,
		canEditScore,
		canEditAssignee,
		canEditGameStatus,
		canViewSla,
	} = gameUploadsPermissions;
	const canEditBasicGameData = canEditGameUploads || canEditVip || canEditHidden || canEditScore;
	const statusAllowsPriorityEdit = useMemo(
		() => isPriorityEditableForStatus(gameData.currentGameAnalysis?.status),
		[gameData.currentGameAnalysis?.status],
	);

	const cannotEditGameUploadsTitle = _("discover.gamesTable.permission.cannotEditGameUploads");
	const cannotEditAssigneeTitle = _("discover.gamesTable.permission.cannotEditAssignee");
	const cannotEditGameStatusTitle = _("discover.gamesTable.permission.cannotEditGameStatus");

	const patchMutation = client.patchDiscoverGame.useMutation();

	const invalidateGameDetails = useCallback(
		async () =>
			Promise.all([
				client.queryClient.invalidateQueries({
					predicate: (query) => query.queryKey.includes(DISCOVER_GAME_V2_DETAIL_QUERY_KEY),
				}),
				client.queryClient.invalidateQueries({
					predicate: (query) => query.queryKey.includes(DISCOVER_GAME_HISTORY_NOTES_QUERY_KEY),
				}),
			]),
		[],
	);

	const handlePatchSuccess = useCallback(async () => {
		await invalidateGameDetails();
		addSuccessToast(_("discover.gameDetail.toast.updated"));
	}, [addSuccessToast, invalidateGameDetails]);

	const handlePatchError = useCallback(
		(error: unknown) => {
			const message =
				typeof error === "object" && error !== null && "message" in error && typeof (error as { message?: unknown }).message === "string"
					? (error as { message: string }).message
					: _("discover.gameDetail.toast.updateFailed");
			addErrorToast(message);
		},
		[addErrorToast],
	);

	const handlePatch = useCallback(
		<Field extends DiscoverGameInlinePatchField>(
			field: Field,
			value: DiscoverGameDetailInlinePatchValue[Field],
		) => {
			patchMutation.mutate(
				{
					id: gameData.id,
					body: {
						id: gameData.id,
						requestedFields: [field],
						[field]: value,
					} as DiscoverGamePatchRequest,
				},
				{
					onSuccess: handlePatchSuccess,
					onError: handlePatchError,
				},
			);
		},
		[patchMutation, gameData.id, handlePatchSuccess, handlePatchError],
	);

	const handleMetadataPatch = useCallback(
		(body: Omit<DiscoverGamePatchRequest, "id">) => {
			patchMutation.mutate(
				{ id: gameData.id, body: { ...body, id: gameData.id } },
				{
					onSuccess: async () => {
						await handlePatchSuccess();
						setIsEditingBasicGameData(false);
					},
					onError: handlePatchError,
				},
			);
		},
		[patchMutation, gameData.id, handlePatchSuccess, handlePatchError],
	);

	const { data: clubMetadataOptionsResp } = client.getDiscoverGamesV2ClubMetadataUserOptions.useQuery({});
	const { data: assigneeOptionsResp } = client.getDiscoverGamesV2AssigneeOptions.useQuery({});
	const { data: gameStatusOptionsResp } = client.getDiscoverGamesV2GameStatusOptions.useQuery({});
	const { data: metadataOptionsResponse } = client.getDiscoverGameMetadataOptions.useQuery(
		{ id: String(gameData.id) },
		{ enabled: canEditGameUploads },
	);

	const clubMetadataOptions: UserOption[] = useMemo(
		() => (clubMetadataOptionsResp?.data ?? []).map((o) => ({ value: String(o.value), label: o.label ?? "" })),
		[clubMetadataOptionsResp],
	);
	const assigneeOptions: UserOption[] = useMemo(
		() => (assigneeOptionsResp?.data ?? []).map((o) => ({ value: String(o.value), label: o.label ?? "" })),
		[assigneeOptionsResp],
	);
	const gameStatusOptions: UserOption[] = useMemo(() => {
		const all = (gameStatusOptionsResp?.data ?? []).map((o) => ({ value: String(o.value), label: o.label ?? "" }));
		if (gameData.gameStatus === DiscoverGameStatus.DONE) {
			return all.filter((o) => o.value === DiscoverGameStatus.GAME_CREATED_WITH_ISSUES);
		}
		return all;
	}, [gameStatusOptionsResp, gameData.gameStatus]);

	const allGameStatusOptions: UserOption[] = useMemo(
		() => (gameStatusOptionsResp?.data ?? []).map((o) => ({ value: String(o.value), label: o.label ?? "" })),
		[gameStatusOptionsResp],
	);

	const priorityOptions: SelectOption[] = useMemo(
		() => DISCOVER_GAMES_PRIORITY_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
		[],
	);

	const contentProps = {
		id: gameData.id,
		mutation: patchMutation,
		invalidationKey: DISCOVER_GAME_V2_DETAIL_QUERY_KEY,
	};

	const tabs = [
		{ label: _("discover.gameDetail.tab.basicData"), key: "basic" },
		{ label: _("discover.gameDetail.tab.activity"), key: "activity" },
		{ label: _("discover.gameDetail.tab.video"), key: "video" },
		{ label: _("discover.gameDetail.tab.gameAnalysis"), key: "analysis" },
		{ label: _("discover.gameDetail.tab.links"), key: "links" },
		{ label: _("discover.gameDetail.tab.teamsheetInfo"), key: "teamsheet" },
	];

	const homeTeamLabel = _("discover.gameDetail.field.homeTeam");
	const awayTeamLabel = _("discover.gameDetail.field.awayTeam");

	const basicFlagDisplayProps = [
		{
			title: _("discover.gameDetail.field.isVip"),
			type: "boolean" as const,
			value: gameData.isVip,
		},
		{
			title: _("discover.gameDetail.field.isHidden"),
			type: "boolean" as const,
			value: gameData.isHidden,
		},
		{
			title: _("discover.gameDetail.field.isHiddenForScout"),
			type: "boolean" as const,
			value: gameData.isHiddenForScout,
		},
		{
			title: _("discover.gameDetail.field.startsOnLeft"),
			type: "string" as const,
			value:
				gameData.homeTeamStartsOnLeft !== null
					? gameData.homeTeamStartsOnLeft
						? homeTeamLabel
						: awayTeamLabel
					: "-",
		},
	];

	const alwaysReadOnlyBasicProps = [
		{
			title: _("discover.gameDetail.field.createdAt"),
			type: "datetime" as const,
			value: gameData.createdAt,
		},
		{
			title: _("discover.gameDetail.field.createdBy"),
			type: "string" as const,
			value: gameData.createdBy?.name ?? "-",
		},
		{
			title: _("discover.gameDetail.field.gamePage"),
			type: "link",
			formatter: () => (
				<Link to={`/game/${gameData.id}`} target="_blank" className="button-link">
					{gameData.id}
				</Link>
			),
		},
		{ title: _("discover.gameDetail.field.description"), type: "string", value: gameData.description ?? "-" },
		{ title: _("discover.gameDetail.field.isDiscover"), type: "boolean", value: gameData.isDiscover },
		{ title: _("discover.gameDetail.field.isFrozen"), type: "boolean", value: gameData.isFrozen },
		...(canViewSla
			? [
				{
					title: _("discover.gameDetail.field.contentSla"),
					type: "string" as const,
					value: gameData.slaContent !== null ? formatDaysHoursMinutesFromTotalSeconds(gameData.slaContent) : "-",
				},
				{
					title: _("discover.gameDetail.field.expansionSla"),
					type: "string" as const,
					value: gameData.slaExpansion !== null ? formatDaysHoursMinutesFromTotalSeconds(gameData.slaExpansion) : "-",
				},
			]
			: []),
		{ title: _("discover.gameDetail.field.numContentErrors"), type: "string", value: String(gameData.numberOfContentErrors) },
		{ title: _("discover.gameDetail.field.numTaggingErrors"), type: "string", value: String(gameData.numberOfTaggingErrors) },
		{ title: _("discover.gameDetail.field.analysisTurnaround"), type: "string", value: _("discover.gameDetail.field.blank") },
		{ title: _("discover.gameDetail.field.sla"), type: "string", value: _("discover.gameDetail.field.blank") },
	];

	return (
		<div className="discoverGameDetailV2">
			{/* Header bar with editable dropdowns */}
			<div className="discoverGameDetailV2-header">
				<div className="discoverGameDetailV2-headerField">
					<label className="discoverGameDetailV2-headerLabel">{_("discover.gameDetail.header.countryManager")}</label>
					{canEditGameUploads ? (
						<SelectComponent
							placeholder={_("discover.gameDetail.placeholder.none")}
							value={clubMetadataOptions.find((o) => o.value === String(gameData.countryManager?.id ?? "")) ?? null}
							items={clubMetadataOptions}
							isCleareable
							isDisabled={patchMutation.isLoading}
							handleChange={(option) => {
								const val = option?.value != null ? Number(option.value) : null;
								handlePatch(DiscoverGameInlinePatchField.ClubManagerId, val);
							}}
						/>
					) : (
						<span className="discoverGameDetailV2-readOnlyValue" title={cannotEditGameUploadsTitle}>
							{gameData.countryManager?.name ?? _("discover.gameDetail.placeholder.none")}
						</span>
					)}
				</div>
				<div className="discoverGameDetailV2-headerField">
					<label className="discoverGameDetailV2-headerLabel">{_("discover.gameDetail.header.contentManager")}</label>
					{canEditGameUploads ? (
						<SelectComponent
							placeholder={_("discover.gameDetail.placeholder.none")}
							value={clubMetadataOptions.find((o) => o.value === String(gameData.contentManager?.id ?? "")) ?? null}
							items={clubMetadataOptions}
							isCleareable
							isDisabled={patchMutation.isLoading}
							handleChange={(option) => {
								const val = option?.value != null ? Number(option.value) : null;
								handlePatch(DiscoverGameInlinePatchField.ContentManagerId, val);
							}}
						/>
					) : (
						<span className="discoverGameDetailV2-readOnlyValue" title={cannotEditGameUploadsTitle}>
							{gameData.contentManager?.name ?? _("discover.gameDetail.placeholder.none")}
						</span>
					)}
				</div>
				<div className="discoverGameDetailV2-headerField">
					<label className="discoverGameDetailV2-headerLabel">{_("discover.gameDetail.header.assignee")}</label>
					{canEditAssignee ? (
						<SelectComponent
							placeholder={_("discover.gameDetail.placeholder.none")}
							value={assigneeOptions.find((o) => o.value === String(gameData.assignee?.id ?? "")) ?? null}
							items={assigneeOptions}
							isCleareable
							isDisabled={patchMutation.isLoading}
							handleChange={(option) => {
								const val = option?.value != null ? Number(option.value) : null;
								handlePatch(DiscoverGameInlinePatchField.AssigneeId, val);
							}}
						/>
					) : (
						<span className="discoverGameDetailV2-readOnlyValue" title={cannotEditAssigneeTitle}>
							{gameData.assignee?.name ?? _("discover.gameDetail.placeholder.none")}
						</span>
					)}
				</div>
				<div className="discoverGameDetailV2-headerField discoverGameDetailV2-headerField--status">
					<label className="discoverGameDetailV2-headerLabel">{_("discover.gameDetail.header.status")}</label>
					{canEditGameStatus ? (
						<div
							className={
								gameStatusGradeColor(gameData.gameStatus)
									? "discoverGamesTableV3-gameStatusGrade discoverGamesTableV3-gameStatusGrade--active"
									: "discoverGamesTableV3-gameStatusGrade"
							}
							style={
								gameStatusGradeColor(gameData.gameStatus)
									? ({ ["--dgs-game-status-color" as string]: gameStatusGradeColor(gameData.gameStatus) } as CSSProperties)
									: undefined
							}
						>
							<SelectComponent
								placeholder={_("discover.gameDetail.placeholder.none")}
								value={allGameStatusOptions.find((o) => o.value === (gameData.gameStatus ?? "")) ?? null}
								items={gameStatusOptions}
								isCleareable
								isDisabled={patchMutation.isLoading}
								handleChange={(option) => {
									const raw = option?.value;
									const val = isEmptyOrNull(raw) ? null : String(raw);
									handlePatch(DiscoverGameInlinePatchField.GameStatus, val as GameStatus | null);
								}}
							/>
						</div>
					) : (
						<span
							className="discoverGameDetailV2-readOnlyValue"
							title={cannotEditGameStatusTitle}
							style={
								gameStatusGradeColor(gameData.gameStatus)
									? { color: gameStatusGradeColor(gameData.gameStatus) }
									: undefined
							}
						>
							{allGameStatusOptions.find((o) => o.value === (gameData.gameStatus ?? ""))?.label ??
								gameData.gameStatus ??
								_("discover.gameDetail.placeholder.none")}
						</span>
					)}
				</div>
				<div className="discoverGameDetailV2-headerField discoverGameDetailV2-headerField--priority">
					<label className="discoverGameDetailV2-headerLabel">{_("discover.gameDetail.header.priority")}</label>
					{canEditGameUploads ? (
						<SelectComponent
							placeholder={_("discover.gameDetail.placeholder.none")}
							value={
								priorityOptions.find(
									(o) => o.value === discoverGameDetailDisplayPriority(gameData.priority),
								) ?? null
							}
							items={priorityOptions}
							isDisabled={patchMutation.isLoading || !statusAllowsPriorityEdit}
							handleChange={(option) => {
								if (isNullOrUndefined(option?.value)) {
									return;
								}
								const next = typeof option.value === "number" ? option.value : Number(option.value);
								if (Number.isNaN(next)) {
									return;
								}
								const current = gameData.priority ?? null;
								if (current === next) {
									return;
								}
								handlePatch(DiscoverGameInlinePatchField.Priority, next);
							}}
						/>
					) : (
						<span className="discoverGameDetailV2-readOnlyValue" title={cannotEditGameUploadsTitle}>
							{discoverGameDetailDisplayPriority(gameData.priority)}
						</span>
					)}
				</div>
			</div>

			{/* Tab bar */}
			<Tabs items={tabs} active={tab} onClick={(key) => setTab(key)} />

			{/* Tab content */}
			{tab === "basic" && (
				<div className="discover-card">
					<div className="cell-grid">
						{canEditBasicGameData && isEditingBasicGameData ? (
							<BasicGameMetadataEditors
								gameData={gameData}
								metadataOptions={metadataOptionsResponse?.data}
								isSaving={patchMutation.isLoading}
								canEditGameUploads={canEditGameUploads}
								canEditVip={canEditVip}
								canEditHidden={canEditHidden}
								canEditScore={canEditScore}
								onPatch={handleMetadataPatch}
								onCancel={() => setIsEditingBasicGameData(false)}
							/>
						) : (
							<>
								<ContentCell {...contentProps} title={_("discover.gameDetail.field.date")} type="datetime" value={gameData.date} />
								<ContentCell
									{...contentProps}
									title={_("discover.gameDetail.field.score")}
									type="string"
									formatter={() => `${gameData.home?.score ?? "-"} : ${gameData.away?.score ?? "-"}`}
								/>
								<ContentCell
									{...contentProps}
									title={_("discover.gameDetail.field.type")}
									type="string"
									value={gameData.type ?? "-"}
									formatter={() => gameTypeLabel(gameData.type, metadataOptionsResponse?.data?.gameTypes)}
								/>
								<ContentCell {...contentProps} title={_("discover.gameDetail.field.league")} type="string" value={gameData.league?.name ?? "-"} />
								<ContentCell {...contentProps} title={_("discover.gameDetail.field.ageCategory")} type="string" value={gameData.ageCategory ?? "-"} />
								<ContentCell
									{...contentProps}
									title={_("discover.gameDetail.field.owners")}
									type="string"
									value={gameData.owners.map((owner) => owner.name).join(", ") || "-"}
								/>
								{basicFlagDisplayProps.map((props) => (
									<ContentCell key={props.title} {...contentProps} {...props} />
								))}
								{canEditBasicGameData && (
									<div className="basicGameMetadataEditors-saveAction form__footer">
										<button
											className="btn"
											type="button"
											onClick={() => setIsEditingBasicGameData(true)}
										>
											{_("discover.gameDetail.btn.editGame")}
										</button>
									</div>
								)}
							</>
						)}
						{alwaysReadOnlyBasicProps.map((props) => (
							<ContentCell key={props.title} {...contentProps} {...props} />
						))}
					</div>
				</div>
			)}

			{tab === "activity" && (
				<DiscoverGameHistoryTab gameId={gameData.id} />
			)}

			{tab === "video" && (
				<DiscoverGameVideoTab gameData={gameData} />
			)}

			{tab === "analysis" && (
				<DiscoverGameAnalysisTab gameData={gameData} />
			)}

			{tab === "links" && (
				<DiscoverGameLinksTab gameData={gameData} />
			)}

			{tab === "teamsheet" && (
				<DiscoverGameTeamsheetTab gameData={gameData} />
			)}
		</div>
	);
}
